import React from "react";
import { Box, Divider, Typography } from "@mui/material";
import { toast } from "react-toastify";
import CustomDialog from "../CustomDialog";
import CustomButton from "../CustomButton";
import CustomSelect from "../CustomSelect";
import CustomTextField from "../CustomTextField";
import CustomLabel from "../CustomLabel";
import { COLORS } from "../../assets/theme";
import {
  MATERIALIZED_AHEAD_DAYS,
  MAX_ENROLL_SESSIONS,
  enrollInClass,
  isEnrollBlockedError,
  quoteEnrollment,
  readSessionsAvailable,
} from "../../services/ClassesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatTableDate } from "../../utils/tableDateFormat";
import { ClassDefinition, EnrollQuote, EnrollResult } from "./types";
import { useClientOptions } from "./useClassOptions";
import { formatMoney, formatTime, sessionRowKey } from "./classUtils";

interface ClassEnrollDialogProps {
  open: boolean;
  businessId?: string;
  classItem: ClassDefinition | null;
  /** Preselects the client — used when opening from a session's roster panel. */
  clientId?: string;
  clientName?: string;
  onClose: () => void;
  onEnrolled?: () => void;
}

const DEFAULT_SESSION_COUNT = 8;

/**
 * Enrollment buys a block of N sessions rather than "the whole series" — an
 * open-ended class has no series length to charge against. The quote step is
 * the point: it commits nothing and shows exactly how the block splits between
 * the client's packages and a new invoice before anything is spent.
 */
const ClassEnrollDialog: React.FC<ClassEnrollDialogProps> = ({
  open,
  businessId,
  classItem,
  clientId: presetClientId,
  clientName: presetClientName,
  onClose,
  onEnrolled,
}) => {
  const lockedClient = Boolean(presetClientId);
  const { options: clientOptions, onSearchInput: onClientSearchInput } =
    useClientOptions(
    businessId,
    open && !lockedClient
  );
  const [clientId, setClientId] = React.useState("");
  const [sessionCount, setSessionCount] = React.useState(
    String(DEFAULT_SESSION_COUNT)
  );
  const [quote, setQuote] = React.useState<EnrollQuote | null>(null);
  const [quoting, setQuoting] = React.useState(false);
  const [confirming, setConfirming] = React.useState(false);
  const [result, setResult] = React.useState<EnrollResult | null>(null);
  // Learned from the first quote — an open-ended class reports a large number,
  // so this only really bites on an ended series or one with an end date.
  const [sessionsAvailable, setSessionsAvailable] = React.useState<number | null>(
    null
  );
  // Set by the two 400s that mean this pairing can never be enrolled, as
  // opposed to this particular request being wrong.
  const [blockedReason, setBlockedReason] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    setClientId(presetClientId ? String(presetClientId) : "");
    setSessionCount(String(DEFAULT_SESSION_COUNT));
    setQuote(null);
    setResult(null);
    setSessionsAvailable(null);
    setBlockedReason(null);
  }, [open, presetClientId]);

  const parsedCount = Number(sessionCount);
  // The class's own limit narrows the hard 1–200 bound once we know it.
  const maxSessions = Math.min(
    MAX_ENROLL_SESSIONS,
    sessionsAvailable ?? MAX_ENROLL_SESSIONS
  );
  const countIsValid =
    Number.isInteger(parsedCount) && parsedCount >= 1 && parsedCount <= maxSessions;

  // Any change to what's being bought invalidates the price that was quoted
  // for it — never let a stale split sit above a Confirm button.
  const invalidateQuote = () => {
    setQuote(null);
    setResult(null);
    setBlockedReason(null);
  };

  // Typed values are held to the class's limit once a quote has told us what it
  // is, so the ask can't drift back above what the class can actually deliver.
  const applySessionCount = (raw: string) => {
    const parsed = Number(raw);
    if (
      sessionsAvailable !== null &&
      Number.isInteger(parsed) &&
      parsed > sessionsAvailable
    ) {
      setSessionCount(String(sessionsAvailable));
    } else {
      setSessionCount(raw);
    }
    invalidateQuote();
  };

  const handleEnrollError = (error: unknown, fallback: string) => {
    const message = getErrorMessage(error, fallback);
    if (isEnrollBlockedError(error)) {
      setBlockedReason(message);
      // Both blocking 400s carry sessions_available: 0, which keeps the count
      // input capped consistently with the reason it was blocked.
      const available = readSessionsAvailable(error);
      if (available !== null) setSessionsAvailable(available);
    }
    toast.error(message);
  };

  const clientLabel =
    presetClientName ||
    clientOptions.find((option) => option.value === clientId)?.label ||
    "this client";

  const handleQuote = async () => {
    if (!classItem || !clientId || !countIsValid) return;
    setQuoting(true);
    try {
      const priced = await quoteEnrollment(String(classItem.id), {
        clientId,
        sessions: parsedCount,
      });
      if (typeof priced.sessions_available === "number") {
        setSessionsAvailable(priced.sessions_available);
      }

      // A clamp to zero prices nothing. Whether the API expresses "none left"
      // as this or as a 400 it is the same dead end, so it lands in the same
      // blocked state rather than offering a Confirm button for zero sessions.
      if (!(priced.sessions_requested > 0)) {
        setQuote(null);
        setBlockedReason(
          "This class has no sessions left to sell for this client."
        );
        return;
      }

      setQuote(priced);
    } catch (error) {
      setQuote(null);
      handleEnrollError(error, "Failed to price the enrollment.");
    } finally {
      setQuoting(false);
    }
  };

  const handleConfirm = async () => {
    if (!classItem || !clientId || !countIsValid) return;
    setConfirming(true);
    try {
      const response = await enrollInClass(String(classItem.id), {
        clientId,
        sessions: parsedCount,
      });
      setResult(response);
      toast.success(
        `Enrolled for ${response.sessions_requested ?? parsedCount} session${
          (response.sessions_requested ?? parsedCount) === 1 ? "" : "s"
        }.`
      );
      onEnrolled?.();
    } catch (error) {
      handleEnrollError(error, "Failed to enroll the client.");
    } finally {
      setConfirming(false);
    }
  };

  // The rule ran out of dates before the 366-day horizon — selling the rest of
  // the block would sell sessions that can never be used.
  const shortOfDates =
    quote !== null && quote.next_sessions.length < quote.sessions_requested;

  /**
   * The split has to account for every session bought: what a package covers
   * plus what gets invoiced is the whole block, nothing dropped and nothing
   * charged twice. Surfacing a mismatch rather than rendering it as if it were
   * fine is the difference between catching a credit-maths bug and quietly
   * billing someone wrong.
   */
  const splitShortfall = (data: {
    sessions_requested?: number;
    covered_by_package?: number;
    invoiced_count?: number;
  }): number | null => {
    const requested = data.sessions_requested;
    if (typeof requested !== "number") return null;
    const accounted =
      (data.covered_by_package ?? 0) + (data.invoiced_count ?? 0);
    return accounted === requested ? null : requested - accounted;
  };

  /**
   * The ask was reduced to what the class can still deliver. Said before
   * confirming, in both numbers, because "10 sessions" is what the client was
   * told they were buying.
   */
  const renderCappedNotice = (data: {
    capped?: boolean;
    sessions_asked?: number;
    sessions_requested?: number;
    sessions_available?: number;
  }) => {
    if (!data.capped) return null;
    const buying = data.sessions_requested ?? data.sessions_available;
    const left = data.sessions_available ?? buying;
    return (
      <Typography
        sx={{ fontSize: 13.5, color: "#ed6c02", fontWeight: 600, mb: 1 }}
      >
        {`This class only has ${left} session${
          left === 1 ? "" : "s"
        } left — you are buying ${buying}, not ${
          data.sessions_asked ?? parsedCount
        }.`}
      </Typography>
    );
  };

  const renderSplitWarning = (shortfall: number | null) =>
    shortfall === null ? null : (
      <Typography
        sx={{ fontSize: 13, color: "#d32f2f", fontWeight: 600, mt: 1 }}
      >
        {`The split doesn't add up: ${
          shortfall > 0
            ? `${shortfall} session${shortfall === 1 ? "" : "s"} unaccounted for`
            : `${-shortfall} session${
                shortfall === -1 ? "" : "s"
              } counted twice`
        }. Check this against the client's packages before relying on it.`}
      </Typography>
    );

  const renderQuote = (data: EnrollQuote) => (
    <Box sx={{ mt: 2, border: "1px solid #eee", borderRadius: 2, p: 2 }}>
      {renderCappedNotice(data)}
      <Typography sx={{ fontWeight: 700, fontSize: 15, mb: 1 }}>
        {`${data.sessions_requested} session${
          data.sessions_requested === 1 ? "" : "s"
        } — ${data.covered_by_package} from ${
          data.covered_by_package === 1 ? "a package" : "packages"
        }, ${data.invoiced_count} to pay`}
      </Typography>

      {data.package_breakdown.length > 0 && (
        <Box sx={{ mb: 1 }}>
          {data.package_breakdown.map((entry, index) => (
            <Typography
              key={String(entry.client_package_id || index)}
              sx={{ fontSize: 13, color: "#555" }}
            >
              {`• ${entry.sessions ?? 0} × ${
                entry.package_name || "Package"
              }${
                entry.expires_at
                  ? ` (expires ${formatTableDate(entry.expires_at)})`
                  : ""
              }`}
            </Typography>
          ))}
        </Box>
      )}

      {data.invoiced_count > 0 && (
        <Typography sx={{ fontSize: 13, color: "#555" }}>
          {`• ${data.invoiced_count} × ${formatMoney(
            data.unit_price ?? classItem?.price
          )} = `}
          <Box component="span" sx={{ fontWeight: 700 }}>
            {formatMoney(data.invoice_total)}
          </Box>
          {" to invoice"}
        </Typography>
      )}

      {renderSplitWarning(splitShortfall(data))}

      {shortOfDates && (
        <Typography sx={{ fontSize: 13, color: "#ed6c02", fontWeight: 600, mt: 1 }}>
          {`Only ${data.next_sessions.length} date${
            data.next_sessions.length === 1 ? "" : "s"
          } exist before the booking horizon — the rest of this block can't be used.`}
        </Typography>
      )}

      {data.next_sessions.length > 0 && (
        <>
          <Divider sx={{ my: 1.5 }} />
          <Typography sx={{ fontSize: 12, color: "#777", mb: 0.5 }}>
            Next dates
          </Typography>
          {data.next_sessions.slice(0, 6).map((session, index) => (
            <Typography
              key={sessionRowKey(session, index)}
              sx={{ fontSize: 13, color: "#555" }}
            >
              {`${formatTableDate(session.start_at)} · ${formatTime(
                session.start_at
              )}${session.is_virtual ? " (projected)" : ""}`}
            </Typography>
          ))}
          {data.next_sessions.length > 6 && (
            <Typography sx={{ fontSize: 12, color: "#999" }}>
              {`+ ${data.next_sessions.length - 6} more`}
            </Typography>
          )}
        </>
      )}
    </Box>
  );

  const renderResult = (data: EnrollResult) => {
    const bookedNow = data.booked_now?.length ?? 0;
    const requested = data.sessions_requested ?? parsedCount;
    // A quote commits nothing, so the client's package balance can move between
    // pricing and confirming — the charge that actually happened is the one
    // that counts, and any drift from the quote staff approved is called out
    // rather than left for the invoice to reveal.
    const quotedCovered = quote?.covered_by_package;
    const chargedDifferently =
      typeof quotedCovered === "number" &&
      typeof data.covered_by_package === "number" &&
      quotedCovered !== data.covered_by_package;
    // Enrollment and invoicing are separate steps server-side: if the billing
    // service is unreachable the enrollment still succeeds and credits are
    // still spent, but no invoice exists for the chargeable remainder. That is
    // money nobody is being asked for, so it gets said out loud rather than
    // inferred from a missing reference number.
    const invoiceMissing = (data.invoiced_count ?? 0) > 0 && !data.invoice_id;
    return (
      <Box sx={{ mt: 2, border: "1px solid #d6ecd8", bgcolor: "#f5fbf6", borderRadius: 2, p: 2 }}>
        {renderCappedNotice(data)}
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
          {`Block of ${requested} session${requested === 1 ? "" : "s"} bought`}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "#555", mt: 0.5 }}>
          {`${data.covered_by_package ?? 0} covered by package · ${
            data.invoiced_count ?? 0
          } invoiced${data.invoice_id ? ` (invoice ${data.invoice_id})` : ""}`}
        </Typography>

        {invoiceMissing && (
          <Typography
            sx={{ fontSize: 13, color: "#ed6c02", fontWeight: 600, mt: 1 }}
          >
            {`No invoice was raised for the ${data.invoiced_count} chargeable session${
              data.invoiced_count === 1 ? "" : "s"
            }. The enrollment went through and package credits were spent correctly — but nobody has been billed, so raise it manually.`}
          </Typography>
        )}
        {chargedDifferently && (
          <Typography
            sx={{ fontSize: 13, color: "#ed6c02", fontWeight: 600, mt: 1 }}
          >
            {`Quoted ${quotedCovered} from packages, charged ${data.covered_by_package} — the balance changed between the quote and confirming.`}
          </Typography>
        )}

        {renderSplitWarning(splitShortfall(data))}

        <Typography sx={{ fontSize: 13, color: "#555", mt: 1 }}>
          {`Booked into ${bookedNow} session${
            bookedNow === 1 ? "" : "s"
          } now — that's everything inside the next ${MATERIALIZED_AHEAD_DAYS} days. The rest of the block is spent automatically as those dates come around.`}
        </Typography>
      </Box>
    );
  };

  return (
    <CustomDialog
      open={open}
      onClose={onClose}
      paperSx={{ minWidth: { sm: 560 }, maxWidth: 700 }}
      title={`Enroll in ${classItem?.name || "Class"}`}
      actions={
        <>
          <CustomButton label="Close" variant="outlined" onClick={onClose} />
          {!result &&
            !blockedReason &&
            (quote ? (
              <CustomButton
                label="Confirm & Enroll"
                variant="contained"
                bgColor={COLORS.MainColor}
                textColor={COLORS.WhiteText}
                loading={confirming}
                disabled={confirming}
                onClick={handleConfirm}
              />
            ) : (
              <CustomButton
                label="Get Quote"
                variant="contained"
                bgColor={COLORS.MainColor}
                textColor={COLORS.WhiteText}
                loading={quoting}
                disabled={!clientId || !countIsValid || quoting}
                onClick={handleQuote}
              />
            ))}
        </>
      }
    >
      <Typography sx={{ fontSize: 14, color: "#555", mb: 2 }}>
        {`Enrollment buys a block of sessions for ${clientLabel}. Pick how many, check the split, then confirm — nothing is charged until you do.`}
      </Typography>

      <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
        {!lockedClient && (
          <Box sx={{ flex: "1 1 260px" }}>
            <CustomLabel
              text="Client"
              fontSize={14}
              fontWeight={500}
              highlightLabelStar
            />
            <CustomSelect
              value={clientId}
              options={clientOptions}
              onSearchInput={onClientSearchInput}
              onChange={(v: any) => {
                setClientId(String(v ?? ""));
                invalidateQuote();
              }}
              placeholder="Select a client"
              searchable
              disabled={Boolean(result)}
              fullWidth
            />
          </Box>
        )}
        <Box sx={{ flex: "0 1 200px" }}>
          <CustomLabel
            text="Number of sessions"
            fontSize={14}
            fontWeight={500}
            highlightLabelStar
          />
          <CustomTextField
            type="number"
            value={sessionCount}
            onChange={(e: any) => applySessionCount(e.target.value)}
            disabled={Boolean(result) || Boolean(blockedReason)}
            inputProps={{ min: 1, max: maxSessions }}
            error={Boolean(sessionCount) && !countIsValid}
            helperText={
              Boolean(sessionCount) && !countIsValid
                ? `Between 1 and ${maxSessions}.`
                : sessionsAvailable !== null &&
                  sessionsAvailable < MAX_ENROLL_SESSIONS
                ? `${sessionsAvailable} left on this class.`
                : undefined
            }
            fullWidth
          />
        </Box>
      </Box>

      {blockedReason ? (
        <Box
          sx={{
            mt: 2,
            border: "1px solid #f2d0d0",
            bgcolor: "#fdf4f4",
            borderRadius: 2,
            p: 1.75,
          }}
        >
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#8a3a3a" }}>
            {blockedReason}
          </Typography>
          <Typography sx={{ fontSize: 13, color: "#a05a5a", mt: 0.5 }}>
            Nothing can be sold for this client on this class — pick a different
            client, or extend the class's schedule first.
          </Typography>
        </Box>
      ) : result ? (
        renderResult(result)
      ) : quote ? (
        renderQuote(quote)
      ) : null}
    </CustomDialog>
  );
};

export default ClassEnrollDialog;
